import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  galleryApis,
  GalleryFile,
  GalleryFolder,
  UpdateFileParams,
  BulkUpdateAssetTypesParams,
} from '../../../gateways/breadstack/galleryApis'

export const galleryQueryKeys = {
  all: () => ['galleries'] as const,
  folders: (orgId?: string) => [...galleryQueryKeys.all(), 'folders', orgId] as const,
  files: (orgId?: string, folderId?: string) => [...galleryQueryKeys.all(), 'files', orgId, folderId] as const,
  filesPage: (
    orgId?: string,
    folderId?: string,
    page?: number,
    search?: string,
    contentType?: string,
    assetType?: string,
  ) => [...galleryQueryKeys.files(orgId, folderId), page, search ?? '', contentType ?? '', assetType ?? ''] as const,
  publicFilesPage: (
    storeId?: string,
    orgId?: string,
    page?: number,
    search?: string,
    contentType?: string,
    assetType?: string,
  ) => [...galleryQueryKeys.all(), 'public-files', storeId, orgId, page, search ?? '', contentType ?? '', assetType ?? ''] as const,
  spatulaFilesPage: (
    storeId?: string,
    orgId?: string,
    page?: number,
    search?: string,
    contentType?: string,
    assetType?: string,
  ) => [...galleryQueryKeys.all(), 'spatula-files', storeId, orgId, page, search ?? '', contentType ?? '', assetType ?? ''] as const,
}

interface GalleryFoldersResponse {
  folders: GalleryFolder[]
  unsortedFileCount?: number
}

export interface GalleryFilesResponse {
  files: GalleryFile[]
  total_pages: number
  page: number
}

export const useGalleryFolders = (orgId?: string, enabled = true) => {
  return useQuery({
    queryKey: galleryQueryKeys.folders(orgId),
    queryFn: async () => {
      const response = await galleryApis.getFolders()
      return {
        folders: response.folders || [],
        unsortedFileCount: response.unsortedFileCount || 0,
      } as GalleryFoldersResponse
    },
    enabled: enabled && Boolean(orgId),
  })
}

interface UseGalleryFilesParams {
  orgId?: string
  folderId?: string
  page?: number
  search?: string
  contentType?: string
  assetType?: string
  enabled?: boolean
}

export const useGalleryFiles = ({
  orgId,
  folderId,
  page = 1,
  search = '',
  contentType = '',
  assetType = '',
  enabled = true,
}: UseGalleryFilesParams) => {
  return useQuery({
    queryKey: galleryQueryKeys.filesPage(orgId, folderId, page, search, contentType, assetType),
    queryFn: async () => {
      const filters: { search_parameter?: string; content_type?: string; asset_type?: string } = {}
      if (search) filters.search_parameter = search
      if (contentType) filters.content_type = contentType
      if (assetType) filters.asset_type = assetType

      const response = await galleryApis.getFiles({
        folder_id: folderId,
        limit: 25,
        page,
        filters: Object.keys(filters).length > 0 ? filters : undefined,
      })
      return response
    },
    enabled: enabled && Boolean(orgId && folderId),
  })
}

interface UsePublicGalleryFilesParams {
  storeId?: string
  orgId?: string
  page?: number
  search?: string
  contentType?: string
  assetType?: string
  enabled?: boolean
}

export const usePublicGalleryFiles = ({
  storeId,
  orgId,
  page = 1,
  search = '',
  contentType = '',
  assetType = '',
  enabled = true,
}: UsePublicGalleryFilesParams) => {
  return useQuery({
    queryKey: galleryQueryKeys.publicFilesPage(storeId, orgId, page, search, contentType, assetType),
    queryFn: async () => {
      const filters: { search_parameter?: string; content_type?: string; asset_type?: string } = {}
      if (search) filters.search_parameter = search
      if (contentType) filters.content_type = contentType
      if (assetType) filters.asset_type = assetType

      return galleryApis.getPublicFiles({
        store_id: storeId!,
        organization_id: orgId!,
        limit: 28,
        page,
        filters: Object.keys(filters).length > 0 ? filters : undefined,
      })
    },
    enabled: enabled && Boolean(storeId && orgId),
  })
}

// Product picker only: uncopied Breadstack catalog images matching the search.
export const useSpatulaCatalogFiles = ({
  storeId,
  orgId,
  page = 1,
  search = '',
  contentType = '',
  assetType = '',
  enabled = true,
}: UsePublicGalleryFilesParams) => {
  return useQuery({
    queryKey: galleryQueryKeys.spatulaFilesPage(storeId, orgId, page, search, contentType, assetType),
    queryFn: async () =>
      galleryApis.getSpatulaCatalogFiles({
        store_id: storeId!,
        organization_id: orgId!,
        limit: 28,
        page,
        filters: { search_parameter: search, content_type: contentType, asset_type: assetType },
      }),
    enabled: enabled && Boolean(storeId && orgId && search.trim()),
  })
}

export const useCreateFile = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: Parameters<typeof galleryApis.createFile>[0]) =>
      galleryApis.createFile(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: galleryQueryKeys.all() })
    },
  })
}

export const useNotifyUploaded = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: Parameters<typeof galleryApis.notifyUploaded>[0]) =>
      galleryApis.notifyUploaded(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: galleryQueryKeys.all() })
    },
  })
}

export const useDeleteFile = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: Parameters<typeof galleryApis.deleteFile>[0]) =>
      galleryApis.deleteFile(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: galleryQueryKeys.all() })
    },
  })
}

export const useUpdateFile = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: UpdateFileParams) => galleryApis.updateFile(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: galleryQueryKeys.all() })
    },
  })
}

// Bulk-replace asset_types across many tenant-owned files. The media list itself
// is refreshed by the consumer's onListReload (removeQueries + accumulator reset);
// invalidating galleryQueryKeys.all() here just keeps the GalleryPicker's cache fresh.
export const useBulkUpdateAssetTypes = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: BulkUpdateAssetTypesParams) => galleryApis.bulkUpdateAssetTypes(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: galleryQueryKeys.all() })
    },
  })
}
